import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import SiteActionSheet from '../components/SiteActionSheet';
import { StatCard } from '../components/metrics';
import { Button } from '../components/ui/button';
import { SITE_STATUS, POSITIONS, ACTION_LABELS } from '../lib/status';
import { Loader2, MapPin, Camera, History, Wrench, ShieldCheck, TriangleAlert, Boxes, PlusCircle, ExternalLink } from 'lucide-react';

export default function SiteDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canAct = ['admin', 'manager', 'operator'].includes(user.role);
  const [data, setData] = useState(null);
  const [sheet, setSheet] = useState(false);

  const load = useCallback(() => api.get(`/sites/${id}`).then((r) => setData(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('sync-updated', h);
    return () => window.removeEventListener('sync-updated', h);
  }, [load]);

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const { site, lift, tower, society, history, campaign, performance } = data;
  const cfg = SITE_STATUS[site.current_status] || SITE_STATUS.not_installed;
  const gallery = history.flatMap((h) => (h.file_ids || []).map((f) => ({ f, h })));

  return (
    <div className="space-y-5">
      <Breadcrumbs items={[
        { label: 'Societies', to: '/societies' }, { label: society.name, to: `/societies/${society.id}` },
        { label: tower.name, to: `/towers/${tower.id}` }, { label: lift.name, to: `/lifts/${lift.id}` },
        { label: POSITIONS[site.position_type] },
      ]} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-primary flex items-center gap-2"><Boxes className="h-3.5 w-3.5" /> {society.name} · {tower.name} · {lift.name}</p>
          <h1 className="font-head text-3xl font-bold">{POSITIONS[site.position_type]}</h1>
          <div className="mt-1 flex items-center gap-2">
            <span className={`rounded-md border px-2 py-1 text-xs font-mono uppercase ${cfg.badge}`}>{cfg.label}</span>
            {campaign && <span className="text-sm text-muted-foreground">· {campaign.name} ({campaign.client_name})</span>}
          </div>
        </div>
        {canAct && <Button data-testid="site-detail-log-action" onClick={() => setSheet(true)}><PlusCircle className="mr-1.5 h-4 w-4" /> Log Action</Button>}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total Events" value={performance.total_events} icon={History} />
        <StatCard label="Damages" value={performance.damages} icon={TriangleAlert} tone="text-rose-400" />
        <StatCard label="Repairs" value={performance.repairs} icon={Wrench} tone="text-cyan-400" />
        <StatCard label="Inspections" value={performance.inspections} icon={ShieldCheck} tone="text-emerald-400" />
      </div>

      {(performance.first_installed_at || performance.last_action_at) && (
        <div className="flex flex-wrap gap-4 rounded-xl border border-border bg-card p-4 text-sm">
          {performance.first_installed_at && <span className="text-muted-foreground">Installed: <b className="text-foreground">{performance.first_installed_at.slice(0, 19).replace('T', ' ')}</b></span>}
          {performance.last_action_at && <span className="text-muted-foreground">Last update: <b className="text-foreground">{performance.last_action_at.slice(0, 19).replace('T', ' ')}</b></span>}
        </div>
      )}

      {gallery.length > 0 && (
        <div>
          <p className="overline mb-2 flex items-center gap-2 text-muted-foreground"><Camera className="h-3.5 w-3.5" /> Proof Gallery</p>
          <div className="flex flex-wrap gap-2">
            {gallery.map(({ f }, i) => (
              <a key={i} href={fileUrl(f)} target="_blank" rel="noreferrer" data-testid={`proof-thumb-${i}`}>
                <img src={fileUrl(f)} alt="" className="h-24 w-24 rounded-lg border border-border object-cover transition hover:scale-105" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="overline mb-2 flex items-center gap-2 text-muted-foreground"><History className="h-3.5 w-3.5" /> Lifecycle History</p>
        <div className="relative space-y-3 border-l-2 border-border pl-5">
          {history.map((h) => {
            const hc = SITE_STATUS[h.new_status] || {};
            return (
              <div key={h.id} data-testid={`site-history-${h.id}`} className="relative rounded-lg border border-border bg-card p-3">
                <span className="absolute -left-[26px] top-4 h-3 w-3 rounded-full border-2 border-background" style={{ background: hc.dot }} />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-head font-bold" style={{ color: hc.dot }}>{ACTION_LABELS[h.action_type] || h.action_type}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{(h.timestamp || h.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">By {h.operator_name || 'operator'}</p>
                {h.address && <p className="mt-1 flex items-start gap-1 text-[11px] text-muted-foreground"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /> {h.address}</p>}
                {h.gps?.lat && <a className="inline-flex items-center gap-1 font-mono text-[10px] text-primary hover:underline" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${h.gps.lat},${h.gps.lng}`}>{h.gps.lat.toFixed(5)}, {h.gps.lng.toFixed(5)} <ExternalLink className="h-2.5 w-2.5" /></a>}
                {h.notes && <p className="mt-1 text-xs">{h.notes}</p>}
                {h.file_ids?.length > 0 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto">
                    {h.file_ids.map((fid) => <img key={fid} src={fileUrl(fid)} alt="" className="h-14 w-14 rounded-md object-cover" />)}
                  </div>
                )}
              </div>
            );
          })}
          {!history.length && <p className="text-sm text-muted-foreground">No lifecycle events recorded yet.</p>}
        </div>
      </div>

      <SiteActionSheet siteId={sheet ? id : null} open={sheet} onClose={(changed) => { setSheet(false); if (changed) load(); }} />
    </div>
  );
}
